import { Component, HostListener, OnInit, TemplateRef } from '@angular/core';
import moment from 'moment';
import {
  ActionType,
  Event,
  EventLog,
  EventLogInfo,
  EventLogResult,
  EventType,
  LogicOperator, SensorGroup, SensorReadingType,
  TimerEventType,
  TimingInterval
} from '@models';
import { EventLogSearchCriteria } from '@models/EventLog/EventLogSearchCriteria.model';
import { TriggerService } from '../trigger.service';
import { ToastrService } from 'ngx-toastr';
import { EventMapper } from '../trigger.mapping';
import { DatePipe } from '@angular/common';
import { DateRanges } from '@models/ranges';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CommonShared } from 'app/shared/classes/common';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

export interface LogsModalParams {
  events: Event[];
  sensorDataTypes: SensorReadingType[];
  sensorGroups: SensorGroup[];
}

@Component({
  standalone: false,
  selector: 'app-logs-modal',
  templateUrl: './logs-modal.component.html',
  styleUrls: ['./logs-modal.component.scss']
})

export class LogsModalComponent implements OnInit, LogsModalParams {

  common = new CommonShared();

  modalDragOn: boolean = false;

  events: Event[];
  sensorDataTypes: SensorReadingType[];
  sensorGroups: SensorGroup[];

  public eventLogCols = [
    {field: 'eventName', header: 'Event Name', width: '20%'},
    {field: 'createdDate', header: 'Date', width: '25%'},
    {field: 'conditions', header: 'Condition(s)', width: '35%'},
    {field: 'actionResults', header: 'Action Results', width: '20%'}
  ];

  currentEventLogSensorReadings: SensorReadingInfo[] = [];
  currentEventNames: string = "";
  screenInnerWidth: number;
  selectedDateRange: DateRange = { startDate: moment(), endDate: moment() };
  sensorDataParams: EventLogSearchCriteria;
  eventLogInfos: EventLogInfo[] = [];
  eventLogs: EventLog[] = [];
  ranges = DateRanges;
  sensorReadingModalRef: BsModalRef;

  public eventLogSensorReadingCols = [
    {field: 'uuid', header: 'UID'},
    {field: 'value', header: 'Value'},
    {field: 'createdate', header: 'Created Date'}
  ];
  public invalidDates: moment.Moment[] = [];

  private eventMapper = new EventMapper();

  constructor(
    public bsModalRef: BsModalRef,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private _triggerService: TriggerService
  ) { }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.screenInnerWidth = window.innerWidth;
  }

  ngOnInit(): void {
    this.screenInnerWidth = window.innerWidth;
    this.datesUpdated('Last 7 Days');
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }

  
  isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));

  datesUpdated(quickPick: string): void {
    if (quickPick === 'Today') {
      this.selectedDateRange = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 2 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(1, 'days'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 7 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(6, 'days'), endDate: moment().endOf('day') };
    }

    var eventIds = this.events.filter(e => e.id > 0).map(e => e.id);
    if (eventIds.length > 0) {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null) {
        this.sensorDataParams = {
          eventIDs: eventIds,
          startDate: this.selectedDateRange.startDate.toDate(),
          endDate: this.selectedDateRange.endDate.toDate(),
          triggered: false
        };
        this.getEventLogs(this.sensorDataParams);
      }
    }
  }


  showSensorReadingsUsedInCalculation(eventLog: EventLog, eventLogResult: EventLogResult, template: TemplateRef<any>) {
    const eventLogInfo = this.eventLogInfos.find(e => e.id == eventLog.id);
    if (eventLogInfo.sensorEventResultInfos == null) {
      return;
    }
    const sensorEventResultInfo = eventLogInfo.sensorEventResultInfos.find(e => e.id === eventLogResult.parentID);
    if (sensorEventResultInfo == null) {
      return;
    }

    this.currentEventLogSensorReadings = sensorEventResultInfo.sensorReadingInfos.sort((t1, t2) => {
      const name1 = t1.uuid.toUpperCase();
      const name2 = t2.uuid.toUpperCase();
      if (name1 > name2) {
        return 1;
      }
      if (name1 < name2) {
        return -1;
      }
      return 0;
    });

    this.currentEventLogSensorReadings.forEach(log => log.createdDate = new Date(moment.utc(log.createdDate).local().toDate()));
    this.sensorReadingModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  exportCsv(value: { data: any; field: string }): string {
    if (value.field === 'createdDate') {
      const dateString = value.data.toLocaleString();
      value.data = new Date(dateString);
      return new DatePipe('en-US').transform(value.data, 'MMM d, y, h:mm:ss a');
    } else {
      return String(value.data);
    }
  }


  private getEventLogs(params: EventLogSearchCriteria) {
    params.endDate = moment(params.endDate).toDate();
    params.startDate = moment(params.startDate).toDate();

    this._triggerService.getEventLogs(params).subscribe(
      results => {
        if (results != null) {
          this.eventLogInfos = results;
          this.eventLogs = [];
          this.eventLogInfos.forEach(e => {
            const eventLog: EventLog = {
              createdDate: moment.utc(e.createdDate).local().toDate(),
              conditionResults: this.common.buildCombinedConditionResults(e, this.sensorGroups, this.screenInnerWidth),
              actionResults: this.common.buildCombinedActionResults(e),
              eventLogInfoID: e.id,
              overallConditionResult: '',
              id: e.id,
              eventName: this.events.find(x => x.id == e.eventID)?.name || ''
            };
            eventLog.overallConditionResult = (e.actionResultInfos.length == 0) ? 'False' : 'True';
            this.eventLogs.push(eventLog);
          });


          this.currentEventNames = this.events.map(e => params.eventIDs.includes(e.id) ? e.name : '').join(', ');
          if (this.currentEventNames.length > 65) {
            this.currentEventNames = this.currentEventNames.substring(0, 65) + '...';
          }
        } else {
          this.toastr.warning('No event logs found for events: ' + this.events.map(e => e.name).join(', '));
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving event logs for ' + this.events.map(e => e.name).join(', '));
      });
  }


  exportEventLogTable() {
    if (this.eventLogs.length < 1) {
      this.toastr.warning('No event logs to export');
      return;
    }

    var exportData: string = 'Date,Condition(s),Action Results' + '\r\n';
    var timeStamp: string = '';
    var eventName: string = '';
    var conditions: string = '';
    var actionResults: string = '';

    this.eventLogs.forEach(x => {
      timeStamp = moment(x.createdDate).format("YYYY-MM-DD HH:mm:ss");
      
      eventName = x.eventName

      conditions = '';
      x.conditionResults.forEach(y => conditions += y.text + ' (' + y.value + ') --- ');

      actionResults = '';
      x.actionResults.forEach(y => actionResults += y.text + ' --- ');

      exportData += timeStamp + ',' + eventName + ',' + conditions + ',' + actionResults + '\r\n';
    });

    if (this.events != undefined && this.events.length > 1) {
      this.saveData(exportData, 'EventLogs_CombinedEvents_' + moment().format("YYYYMMDD_HHmmss") + '.csv');
    }
    else if (this.events != undefined && this.events.length == 1) {
      this.saveData(exportData, this.events[0].name + '_' + moment().format("YYYYMMDD_HHmmss") + '.csv');
    }
  }


  saveData(dataString: string, fileName: string) {
    const blob = new Blob(['\uFEFF', dataString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }


}
